import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle2, Lightbulb, Loader2, ChevronRight, AlertCircle, RefreshCw, Target, SkipForward, ArrowLeft } from 'lucide-react';
import { useFamilyContext } from '../../../contexts/FamilyContext';
import { collegeJourneyService } from '../../../lib/college-journey-service';
import EssayResultsDisplay from './EssayResultsDisplay';

export default function NegotiationPersuasionInterview({ onComplete, assessmentData }) {
  const { currentUser } = useFamilyContext();
  const [phase, setPhase] = useState('intro');
  const [responses, setResponses] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [insights, setInsights] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [savedResponses, setSavedResponses] = useState(null);
  const [showAutoSave, setShowAutoSave] = useState(false);

  const questions = [
    {
      id: 'mundane_negotiation',
      question: "Tell me about the last time you had to negotiate something completely low-stakes - what to watch, where to eat, whose turn it was to do something, etc.",
      placeholder: "Where were you? What were the exact first words you said? What was their response? How did it resolve?",
      stakes: 'low'
    },
    {
      id: 'permission_play',
      question: "Describe a time you had to convince a parent/guardian to let you do something they initially said no to.",
      placeholder: "What was their first reason for saying no? What did you try first that didn't work? What was the final thing that convinced them?",
      stakes: 'high'
    },
    {
      id: 'grade_appeal',
      question: "Have you ever contested a grade, asked for an extension, or negotiated with a teacher about an assignment? Tell me about it.",
      placeholder: "Did you prepare what to say or wing it? What exact words did you use to START the conversation? What happened?",
      stakes: 'high'
    },
    {
      id: 'group_conflict',
      question: "Tell me about a time you were in a group (project, friend group, team, family) where people disagreed and you tried to resolve it or push for a particular direction.",
      placeholder: "What were the actual sides/positions? Did you state your opinion immediately or wait? Who was hardest to convince?",
      stakes: 'medium'
    },
    {
      id: 'advocate_role',
      question: "Describe a time you argued on BEHALF of someone else - defended a friend, spoke up for a sibling, advocated for your team, etc.",
      placeholder: "Why you and not them? What specific points did you make? What was different about arguing for someone else vs. yourself?",
      stakes: 'high'
    },
    {
      id: 'lost_cause',
      question: "Tell me about a negotiation or argument you LOST. Something where you tried to convince someone and completely failed.",
      placeholder: "At what point did you realize you weren't going to win? What argument did you think would work but didn't? Why do you think you lost?",
      stakes: 'high'
    },
    {
      id: 'strategic_timing',
      question: "Tell me about a time you were STRATEGIC about how/when/where you asked for something because you knew it would matter.",
      placeholder: "What made you realize timing/context mattered? What did you wait for? Did you soften them up first? How?",
      stakes: 'low'
    },
    {
      id: 'text_negotiation',
      question: "Describe a time you had to negotiate or argue something entirely over text/chat. Show me the actual conversation (or reconstruct it as close as you can remember).",
      placeholder: "Did you draft and redraft messages? How long did you wait between responses? What did you say differently than you would have in person?",
      stakes: 'low'
    }
  ];

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const wordCount = currentAnswer.trim().split(/\s+/).filter(Boolean).length;
  const hasMinimumDetail = wordCount >= 50;

  // Auto-save indicator effect
  useEffect(() => {
    if (currentAnswer.length > 0) {
      setShowAutoSave(true);
      const timer = setTimeout(() => setShowAutoSave(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [currentAnswer]);

  // Track stakes coverage
  const getStakesCoverage = (currentResponses) => {
    const highStakesQuestions = ['permission_play', 'grade_appeal', 'advocate_role', 'lost_cause'];
    const lowStakesQuestions = ['mundane_negotiation', 'strategic_timing', 'text_negotiation'];
    
    const hasHighStakes = highStakesQuestions.some(q => 
      currentResponses[q] && currentResponses[q].trim().length > 0
    );
    
    const hasLowStakes = lowStakesQuestions.some(q => 
      currentResponses[q] && currentResponses[q].trim().length > 0
    );

    return { hasHighStakes, hasLowStakes };
  };

  const startInterview = () => {
    setPhase('questioning');
  };

  const submitAnswer = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: currentAnswer.trim() || ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const skipQuestion = () => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: ''
    };
    setResponses(newResponses);

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer('');
    } else {
      finishInterview(newResponses);
    }
  };

  const finishInterview = async (finalResponses) => {
    const answeredQuestions = Object.values(finalResponses).filter(
      response => response && response.trim().length > 0
    );
    
    const { hasHighStakes, hasLowStakes } = getStakesCoverage(finalResponses);

    if (answeredQuestions.length < 5) {
      alert(`Please answer at least 5 questions to complete the interview. You've answered ${answeredQuestions.length} questions.`);
      return;
    }

    if (!hasHighStakes || !hasLowStakes) {
      alert('Please answer at least one high-stakes negotiation (with parents/teachers/advocacy) and one low-stakes negotiation (everyday situations) to show your range.');
      return;
    }

    setPhase('processing');
    setIsProcessing(true);

    try {
      const insightData = await generateInsights(finalResponses);
      setInsights(insightData);
      
      try {
        console.log('💾 Saving interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'negotiation_persuasion',
          insightData
        );
        console.log('💾 Interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save interview results:', saveError);
      }
      
      setPhase('results');
      
      if (onComplete) {
        onComplete({
          type: 'negotiation_persuasion',
          responses: finalResponses,
          insights: insightData
        });
      }
    } catch (error) {
      console.error('Error generating insights:', error);
      
      setSavedResponses(finalResponses);
      
      const isParsingError = error.message?.includes('parse') || 
                            error.message?.includes('JSON') ||
                            (error.shouldRetry);
      
      setInsights({
        error: true,
        message: error.isOverloaded 
          ? "The AI service is temporarily busy. Please try again in a moment."
          : isParsingError
          ? "The AI response format was unexpected. Please try again."
          : "Failed to generate insights. Please try again.",
        canRetry: true
      });
      setPhase('error');
    }

    setIsProcessing(false);
  };

  const generateInsights = async (interviewResponses) => {
    try {
      const response = await fetch('/api/essay-ideas/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          interviewType: 'negotiation_persuasion',
          studentName: currentUser?.name || currentUser?.displayName || 'this student',
          responses: interviewResponses,
          userId: currentUser?.id,
          context: {
            personality: assessmentData?.personality,
            assessmentCompleted: !!assessmentData
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.isOverloaded || response.status === 503) {
          const error = new Error('Service temporarily overloaded');
          error.isOverloaded = true;
          throw error;
        }

        throw new Error(errorData.error || 'Failed to generate insights');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling insights API:', error);
      
      error.isNetworkError = error.message.includes('fetch') || error.message.includes('network');
      error.isOverloaded = error.isOverloaded || error.message.includes('overloaded');
      
      throw error;
    }
  };

  const retryAnalysis = async () => {
    setPhase('processing');
    setIsProcessing(true);
    setRetryCount(prev => prev + 1);
    
    try {
      const responsesToAnalyze = savedResponses || responses;
      const insightData = await generateInsights(responsesToAnalyze);
      setInsights(insightData);
      
      try {
        console.log('💾 Saving retry interview results to database...');
        const saveResult = await collegeJourneyService.saveInterviewResults(
          currentUser?.id,
          'negotiation_persuasion',
          insightData
        );
        console.log('💾 Retry interview results saved:', saveResult ? 'Success' : 'Failed (table may not exist)');
      } catch (saveError) {
        console.error('Failed to save retry interview results:', saveError);
      }
      
      setPhase('results');
      
      if (onComplete) {
        onComplete({
          type: 'negotiation_persuasion',
          responses: responsesToAnalyze,
          insights: insightData
        });
      }
    } catch (error) {
      console.error(`Retry attempt ${retryCount + 1} failed:`, error);
      
      setInsights({
        error: true,
        message: `Retry attempt ${retryCount + 1} failed. ${
          error.isOverloaded 
            ? "The service is still busy." 
            : "Please try again."
        }`,
        canRetry: true
      });
      setPhase('error');
    }
    
    setIsProcessing(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      submitAnswer();
    }
  };

  const { hasHighStakes, hasLowStakes } = getStakesCoverage(responses);
  const answeredCount = Object.values(responses).filter(r => r && r.trim().length > 0).length;

  return (
    <div className="min-h-screen bg-stone-50 font-['Reddit_Sans',system-ui,sans-serif]">
      {phase === 'intro' && (
        <>
          {/* Header with Back Navigation */}
          <div className="bg-white border-b-2 border-slate-900">
            <div className="max-w-5xl mx-auto px-8 py-6">
              <button 
                onClick={() => window.history.back()}
                className="flex items-center gap-2 text-slate-700 hover:text-slate-900 transition-colors mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium">Back to Ideas Hub</span>
              </button>
              
              <div className="flex items-start gap-6">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900 mb-2">Negotiation & Persuasion</h1>
                  <p className="text-base text-slate-700">Uncover your distinctive approach to influence and conflict</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-5xl mx-auto px-8 py-12">
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-base text-slate-900 leading-relaxed">
                This interview explores how you navigate persuasion, handle conflict, and adapt your approach based on context and power dynamics.
              </p>
            </div>

            {/* What We're Looking For Section */}
            <div className="mb-12 bg-white border-2 border-slate-900 rounded-xl overflow-hidden">
              <div className="px-6 py-4 bg-slate-900 border-l-4 border-l-indigo-400">
                <h2 className="text-white font-bold text-sm uppercase tracking-wide">What I'm analyzing:</h2>
              </div>
              <div className="p-8 bg-white">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Your tactical approach to persuasion (timing, preparation, adaptation)</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">How you read people and adjust your strategy</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">Your signature moves and patterns across different contexts</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">How you handle power dynamics (with parents, teachers, peers)</p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full bg-indigo-500 mt-2 flex-shrink-0"></div>
                    <p className="text-base text-slate-900">What you've learned from negotiations that didn't work</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Callout */}
            <div className="mb-12 bg-indigo-50 border-2 border-indigo-500 rounded-xl p-6 border-l-4 border-l-indigo-400">
              <div className="flex items-start gap-3">
                <Target className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">Focus on tactics, not outcomes.</p>
                  <p className="text-xs text-slate-800 pt-2">I want the exact words you used, how you timed your ask, what you tried when Plan A failed. Include both low-stakes negotiations (what to watch) and high-stakes (convincing parents, teachers).</p>
                  <p className="text-xs font-semibold text-slate-800 pt-1">Show me your moves; I'll identify your persuasion signature.</p>
                </div>
              </div>
            </div>

            {/* Begin Button */}
            <div>
              <button 
                onClick={startInterview}
                className="px-8 py-4 bg-indigo-400 text-slate-900 border-2 border-slate-900 rounded-lg font-bold text-base hover:bg-indigo-500 transition-all active:scale-95 shadow-sm"
              >
                <MessageSquare className="inline w-5 h-5 mr-3" />
                Let's Begin
              </button>
            </div>
          </div>
        </>
      )}

      {phase === 'questioning' && currentQuestion && (
        <div className="max-w-4xl mx-auto">
          {/* Progress Header */}
          <div className="bg-white border-2 border-slate-900 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="bg-slate-900 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    currentQuestion.stakes === 'high' ? 'bg-red-400' : 
                    currentQuestion.stakes === 'medium' ? 'bg-amber-400' : 
                    'bg-green-400'
                  }`}></div>
                  <span className="text-sm font-bold text-white">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </span>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${
                    currentQuestion.stakes === 'high' ? 'text-red-400' : 
                    currentQuestion.stakes === 'medium' ? 'text-amber-400' : 
                    'text-green-400'
                  }`}>
                    {currentQuestion.stakes}-stakes
                  </span>
                  <span className="text-stone-400 text-xs">
                    • ~{Math.ceil((questions.length - currentQuestionIndex) * 2)} min remaining
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {showAutoSave && (
                    <span className="text-xs text-teal-300 animate-fadeIn">Saved</span>
                  )}
                  <span className="text-sm text-stone-300">{Math.round(progress)}% complete</span>
                </div>
              </div>
            </div>
            <div className="relative h-2 bg-stone-200">
              <div 
                className="absolute inset-y-0 left-0 bg-purple-300 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white border border-slate-900 rounded-xl shadow-sm">
            <div className="p-8 pb-6">
              <h1 className="text-base font-semibold text-slate-900 mb-4 leading-relaxed">
                {currentQuestion.question}
              </h1>

              {/* Guidance Callout */}
              <div className="mb-6 bg-purple-50 border-l-4 border-l-purple-600 rounded-r-lg p-4">
                <p className="text-xs font-semibold text-purple-900 mb-1">What makes a great response:</p>
                <p className="text-xs text-slate-900">
                  Include <span className="font-semibold text-purple-900">exact words</span> you used, <span className="font-semibold text-purple-900">timing details</span>, and <span className="font-semibold text-purple-900">what you tried</span>. Show how you adapted your approach based on the person and context.
                </p>
              </div>

              {/* Textarea */}
              <div className="relative">
                <textarea
                  value={currentAnswer}
                  onChange={(e) => setCurrentAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={currentQuestion.placeholder}
                  className="w-full min-h-[280px] p-4 border-2 border-slate-300 rounded-lg text-slate-900 text-sm placeholder:text-stone-500 focus:outline-none focus:border-purple-600 focus:ring-2 focus:ring-purple-100 transition-all resize-none"
                  autoFocus
                />
                
                {wordCount > 0 && (
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    {hasMinimumDetail ? (
                      <div className="flex items-center gap-1.5 text-teal-600 text-xs font-medium bg-teal-50 px-2 py-1 rounded">
                        <div className="w-1 h-1 bg-teal-600 rounded-full"></div>
                        Good detail
                      </div>
                    ) : (
                      <div className="text-stone-500 text-xs">
                        {wordCount} words • Keep going...
                      </div>
                    )}
                  </div>
                )}
              </div>

              {wordCount === 0 && (
                <button className="mt-3 text-xs text-purple-600 hover:text-purple-700 font-medium">
                  Show me an example response →
                </button>
              )}
            </div>

            {/* Footer */}
            <div className="bg-stone-50 px-8 py-5 border-t border-stone-200 rounded-b-xl">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm">
                  <p className="text-stone-600 text-xs flex items-center gap-2 flex-wrap">
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    {answeredCount} of 5 minimum questions answered
                    <span className="inline-flex gap-2 items-center">
                      <span className={`text-xs ${hasHighStakes ? 'text-green-600' : 'text-amber-600'}`}>
                        {hasHighStakes ? '✓' : '○'} High-stakes
                      </span>
                      <span className={`text-xs ${hasLowStakes ? 'text-green-600' : 'text-amber-600'}`}>
                        {hasLowStakes ? '✓' : '○'} Low-stakes
                      </span>
                    </span>
                    <span className="inline-block w-1 h-1 bg-stone-400 rounded-full"></span>
                    Press Ctrl+Enter to submit
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <button 
                    onClick={skipQuestion}
                    className="px-5 py-2.5 text-slate-700 text-sm font-medium hover:text-slate-900 transition-colors flex items-center gap-1.5"
                  >
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button 
                    onClick={submitAnswer}
                    className="bg-purple-600 text-white px-6 py-2.5 rounded-lg border-2 border-slate-900 font-medium text-sm hover:bg-purple-700 transition-all active:scale-95 flex items-center gap-2 shadow-sm"
                  >
                    {currentQuestionIndex < questions.length - 1 ? 'Next Question' : 'Complete Interview'}
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {currentQuestionIndex < questions.length - 1 && (
                <div className="pt-3 border-t border-stone-200">
                  <div className="flex items-start gap-2">
                    <div className="w-1 h-1 bg-purple-600 rounded-full mt-1.5 flex-shrink-0"></div>
                    <div className="text-xs text-stone-600">
                      <span className="font-semibold text-slate-700">COMING NEXT:</span> {questions[currentQuestionIndex + 1].question}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'processing' && (
        <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-lg text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Analyzing Your Negotiation Patterns</h3>
          <p className="text-slate-600 mb-6">
            We're examining your tactical approach to persuasion, conflict resolution, and power dynamic navigation across different contexts.
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-700">
              <strong>Please wait...</strong> This analysis reveals your distinctive negotiation signature and how you've evolved your approach over time.
            </p>
          </div>
        </div>
      )}

      {phase === 'error' && insights?.error && (
        <div className="bg-red-50 rounded-2xl p-8 border border-red-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Analysis Generation Failed
            </h3>
            <p className="text-red-700 mb-6 text-lg">{insights.message}</p>
            
            <button
              onClick={retryAnalysis}
              disabled={isProcessing}
              className="bg-red-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <RefreshCw className={`w-5 h-5 ${isProcessing ? 'animate-spin' : ''}`} />
              {isProcessing ? 'Retrying...' : `Retry Analysis${retryCount > 0 ? ` (Attempt ${retryCount + 1})` : ''}`}
            </button>
            
            <p className="text-sm text-slate-600 mt-4">
              Your responses have been saved and will be reprocessed when you retry.
            </p>
            
            {retryCount > 2 && (
              <p className="text-sm text-amber-600 mt-2">
                If the issue persists, please try again in a few minutes or contact support.
              </p>
            )}
          </div>
        </div>
      )}

      {phase === 'results' && insights && (
        <EssayResultsDisplay 
          insights={insights} 
          interviewType="negotiation_persuasion"
        />
      )}
    </div>
  );
}
